import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Car,
  Image as ImageIcon,
  X,
  Euro,
  Calendar,
  Gauge,
  Fuel,
  Settings2,
  MapPin,
  Palette,
  Zap,
  Star,
  Eye,
  EyeOff,
  Upload,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Loader2,
  GripVertical,
  Video,
} from "lucide-react";
import axios from "axios";
import { GalleryPhotosPanel, GalleryVideosPanel } from "./AdminGalleryPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FUEL_TYPES = [
  { value: "essence", label: "Essence" },
  { value: "diesel", label: "Diesel" },
  { value: "hybride", label: "Hybride" },
  { value: "electrique", label: "Électrique" },
  { value: "hybride-rechargeable", label: "Hybride Rechargeable" },
];

const TRANSMISSIONS = [
  { value: "manuelle", label: "Manuelle" },
  { value: "automatique", label: "Automatique" },
];

const ORIGINS = [
  { value: "Allemagne", label: "Allemagne" },
  { value: "Belgique", label: "Belgique" },
  { value: "Italie", label: "Italie" },
  { value: "Espagne", label: "Espagne" },
  { value: "Pays-Bas", label: "Pays-Bas" },
  { value: "Corée du Sud", label: "Corée du Sud" },
  { value: "France", label: "France" },
  { value: "Autre", label: "Autre" },
];

const AdminVehiclesPage = () => {
  const navigate = useNavigate();
  const adminToken = localStorage.getItem("adminToken");
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingVehicle, setEditingVehicle] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    brand: "",
    model: "",
    year: new Date().getFullYear(),
    price: "",
    mileage: "",
    fuel_type: "",
    transmission: "",
    power: "",
    color: "",
    origin: "",
    description: "",
    features: [],
    images: [],
    is_available: true,
    is_featured: false,
    is_sold: false,
    is_good_deal: false,
  });
  const [newFeature, setNewFeature] = useState("");
  const [newImageUrl, setNewImageUrl] = useState("");
  const [uploadingImages, setUploadingImages] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const fetchVehicles = async () => {
    setLoading(true);
    const token = localStorage.getItem("adminToken");
    try {
      const response = await axios.get(`${API}/admin/available-vehicles?token=${token}`);
      setVehicles(response.data);
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      if (error.response?.status === 401) {
        localStorage.removeItem("adminToken");
        navigate("/espace-pro-fastandcar");
      }
      toast.error("Erreur de chargement des véhicules");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Gestion des Véhicules - Espace Pro | Fast&Car";
    
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.name = 'robots';
      document.head.appendChild(metaRobots);
    }
    metaRobots.content = 'noindex, nofollow';
    
    fetchVehicles();
    
    return () => {
      if (metaRobots) {
        metaRobots.content = 'index, follow';
      }
    };
  }, []);

  const resetForm = () => {
    setFormData({
      brand: "",
      model: "",
      year: new Date().getFullYear(),
      price: "",
      mileage: "",
      fuel_type: "",
      transmission: "",
      power: "",
      color: "",
      origin: "",
      description: "",
      features: [],
      images: [],
      is_available: true,
      is_featured: false,
      is_sold: false,
      is_good_deal: false,
    });
    setNewFeature("");
    setNewImageUrl("");
  };

  const openCreateDialog = () => {
    setEditingVehicle(null);
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (vehicle) => {
    setEditingVehicle(vehicle);
    setFormData({
      brand: vehicle.brand || "",
      model: vehicle.model || "",
      year: vehicle.year || new Date().getFullYear(),
      price: vehicle.price || "",
      mileage: vehicle.mileage || "",
      fuel_type: vehicle.fuel_type || "",
      transmission: vehicle.transmission || "",
      power: vehicle.power || "",
      color: vehicle.color || "",
      origin: vehicle.origin || "",
      description: vehicle.description || "",
      features: vehicle.features || [],
      images: vehicle.images || [],
      is_available: vehicle.is_available ?? true,
      is_featured: vehicle.is_featured ?? false,
      is_sold: vehicle.is_sold ?? false,
      is_good_deal: vehicle.is_good_deal ?? false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.brand || !formData.model || !formData.price) {
      toast.error("Veuillez remplir les champs obligatoires (Marque, Modèle, Prix)");
      return;
    }

    setSaving(true);
    const token = localStorage.getItem("adminToken");

    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        mileage: formData.mileage ? parseInt(formData.mileage) : null,
        year: parseInt(formData.year),
      };

      if (editingVehicle) {
        await axios.put(`${API}/available-vehicles/${editingVehicle.id}?token=${token}`, payload);
        toast.success("Véhicule mis à jour !");
      } else {
        await axios.post(`${API}/available-vehicles?token=${token}`, payload);
        toast.success("Véhicule ajouté !");
      }

      setDialogOpen(false);
      fetchVehicles();
    } catch (error) {
      console.error("Error saving vehicle:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!vehicleToDelete) return;

    const token = localStorage.getItem("adminToken");
    try {
      await axios.delete(`${API}/available-vehicles/${vehicleToDelete.id}?token=${token}`);
      toast.success("Véhicule supprimé");
      setDeleteConfirmOpen(false);
      setVehicleToDelete(null);
      fetchVehicles();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()],
      });
      setNewFeature("");
    }
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index),
    });
  };

  const addImage = () => {
    if (newImageUrl.trim()) {
      setFormData({
        ...formData,
        images: [...formData.images, newImageUrl.trim()],
      });
      setNewImageUrl("");
    }
  };

  const removeImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index),
    });
  };

  // Handle file upload via drag & drop or file input
  const handleFileUpload = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    const token = localStorage.getItem("adminToken");
    setUploadingImages(true);

    const validFiles = Array.from(files).filter(file => {
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/avif'];
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name}: Type non supporté (JPEG, PNG, WebP, AVIF)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) {
      setUploadingImages(false);
      return;
    }

    try {
      const uploadPromises = validFiles.map(async (file) => {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(
          `${API}/upload-image?token=${token}`,
          formDataUpload,
          {
            headers: { 'Content-Type': 'multipart/form-data' }
          }
        );
        
        return response.data;
      });

      const results = await Promise.all(uploadPromises);
      
      const newImages = results
        .filter(r => r.success)
        .map(r => r.url);

      if (newImages.length > 0) {
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, ...newImages]
        }));
        toast.success(`${newImages.length} photo(s) ajoutée(s)`);
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Erreur lors de l'upload des images");
    } finally {
      setUploadingImages(false);
    }
  }, []);

  // Drag & Drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    handleFileUpload(files);
  }, [handleFileUpload]);

  const handleFileInputChange = (e) => {
    handleFileUpload(e.target.files);
    e.target.value = ''; // Reset input
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div data-testid="admin-vehicles-page" className="pt-32 md:pt-40 min-h-screen bg-zinc-950">
      {/* Header */}
      <section className="py-3 md:py-4 bg-black border-b border-zinc-800 sticky top-16 md:top-24 z-30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate("/espace-pro-fastandcar/dashboard")}
                variant="ghost"
                size="sm"
                className="text-zinc-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour au CRM
              </Button>
              <div className="h-6 w-px bg-zinc-700" />
              <h1 className="font-unbounded text-base md:text-xl font-bold text-white flex items-center gap-2">
                <Car className="w-5 h-5 text-neon-lime" />
                Véhicules Disponibles
              </h1>
              <span className="text-zinc-500 text-sm">
                {vehicles.length} véhicule{vehicles.length !== 1 ? "s" : ""}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={fetchVehicles}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-400"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              </Button>
              <Button
                onClick={openCreateDialog}
                className="bg-neon-lime text-black font-bold"
                data-testid="add-vehicle-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un véhicule
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Vehicles Grid */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4">
          {loading ? (
            <div className="flex justify-center py-20">
              <RefreshCw className="w-8 h-8 text-neon-lime animate-spin" />
            </div>
          ) : vehicles.length === 0 ? (
            <div className="text-center py-20">
              <Car className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">Aucun véhicule</h3>
              <p className="text-zinc-500 mb-6">
                Commencez par ajouter un véhicule disponible à la commande
              </p>
              <Button
                onClick={openCreateDialog}
                className="bg-neon-lime text-black font-bold"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter un véhicule
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {vehicles.map((vehicle) => (
                <div
                  key={vehicle.id}
                  data-testid={`vehicle-card-${vehicle.id}`}
                  className={`bg-zinc-900 border ${
                    vehicle.is_featured ? "border-neon-lime" : "border-zinc-800"
                  } overflow-hidden group hover:border-zinc-600 transition-all`}
                >
                  {/* Image */}
                  <div className="relative aspect-video bg-zinc-800">
                    {vehicle.images && vehicle.images.length > 0 ? (
                      <img
                        src={vehicle.images[0]}
                        alt={`${vehicle.brand} ${vehicle.model}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Car className="w-12 h-12 text-zinc-700" />
                      </div>
                    )}
                    {/* Badges */}
                    <div className="absolute top-2 left-2 flex gap-2">
                      {vehicle.is_featured && (
                        <span className="bg-neon-lime text-black text-xs font-bold px-2 py-1 flex items-center gap-1">
                          <Star className="w-3 h-3" /> À la une
                        </span>
                      )}
                      {vehicle.is_sold && (
                        <span className="bg-red-600 text-white text-xs font-bold px-2 py-1">
                          VENDUE
                        </span>
                      )}
                      {!vehicle.is_available && (
                        <span className="bg-zinc-700 text-white text-xs font-bold px-2 py-1">
                          Masqué
                        </span>
                      )}
                      {vehicle.is_good_deal && (
                        <span className="bg-orange-500 text-white text-[10px] font-bold px-2 py-1 uppercase tracking-wider">
                          Bonne affaire
                        </span>
                      )}
                    </div>
                    {/* Image count */}
                    {vehicle.images && vehicle.images.length > 1 && (
                      <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 flex items-center gap-1">
                        <ImageIcon className="w-3 h-3" />
                        {vehicle.images.length}
                      </span>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-unbounded text-lg font-bold text-white">
                          {vehicle.brand} {vehicle.model}
                        </h3>
                        <p className="text-zinc-500 text-sm">
                          {vehicle.year} • {vehicle.power || "N/A"}
                        </p>
                      </div>
                      <p className="text-neon-lime font-bold text-lg">
                        {formatPrice(vehicle.price)}
                      </p>
                    </div>

                    {/* Details */}
                    <div className="flex flex-wrap gap-2 text-xs text-zinc-500 mb-4">
                      {vehicle.mileage && (
                        <span className="flex items-center gap-1">
                          <Gauge className="w-3 h-3" />
                          {vehicle.mileage.toLocaleString()} km
                        </span>
                      )}
                      {vehicle.fuel_type && (
                        <span className="flex items-center gap-1">
                          <Fuel className="w-3 h-3" />
                          {vehicle.fuel_type}
                        </span>
                      )}
                      {vehicle.transmission && (
                        <span className="flex items-center gap-1">
                          <Settings2 className="w-3 h-3" />
                          {vehicle.transmission}
                        </span>
                      )}
                      {vehicle.origin && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {vehicle.origin}
                        </span>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => openEditDialog(vehicle)}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-zinc-700 text-zinc-400 hover:text-white"
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Modifier
                      </Button>
                      <Button
                        onClick={() => {
                          setVehicleToDelete(vehicle);
                          setDeleteConfirmOpen(true);
                        }}
                        variant="outline"
                        size="sm"
                        className="border-red-800 text-red-500 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ==================== GALERIE PUBLIQUE ==================== */}
      <section className="max-w-7xl mx-auto px-6 pb-12" data-testid="admin-gallery-section">
        <div className="border-t border-zinc-800 pt-10 mb-8">
          <div className="flex items-start gap-3 mb-2">
            <div className="w-1 h-8 bg-neon-lime" />
            <div>
              <h2 className="font-unbounded text-2xl md:text-3xl font-black text-white">
                Galerie publique
              </h2>
              <p className="text-zinc-400 text-sm mt-1">
                Photos et vidéos affichées sur la page <span className="text-neon-lime">/galerie</span> du site (nos réalisations)
              </p>
            </div>
          </div>
        </div>

        <Tabs defaultValue="gallery-photos" className="w-full">
          <TabsList className="bg-zinc-900 border border-zinc-800 mb-6">
            <TabsTrigger
              value="gallery-photos"
              data-testid="tab-gallery-photos"
              className="data-[state=active]:bg-neon-lime data-[state=active]:text-black font-bold"
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              Photos
            </TabsTrigger>
            <TabsTrigger
              value="gallery-videos"
              data-testid="tab-gallery-videos"
              className="data-[state=active]:bg-neon-lime data-[state=active]:text-black font-bold"
            >
              <Video className="w-4 h-4 mr-2" />
              Vidéos
            </TabsTrigger>
          </TabsList>
          <TabsContent value="gallery-photos">
            <GalleryPhotosPanel token={adminToken} />
          </TabsContent>
          <TabsContent value="gallery-videos">
            <GalleryVideosPanel token={adminToken} />
          </TabsContent>
        </Tabs>
      </section>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-unbounded text-xl flex items-center gap-2">
              <Car className="w-6 h-6 text-neon-lime" />
              {editingVehicle ? "Modifier le véhicule" : "Ajouter un véhicule"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 py-4">
            {/* Left Column */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-1">
                    Marque <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.brand}
                    onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                    placeholder="Ex: BMW, Audi..."
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="vehicle-brand-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-1">
                    Modèle <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                    placeholder="Ex: M3, RS6..."
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="vehicle-model-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-1">
                    <Calendar className="w-4 h-4 text-neon-lime" />
                    Année
                  </Label>
                  <Input
                    type="number"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                    min="1900"
                    max="2030"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-1">
                    <Euro className="w-4 h-4 text-neon-lime" />
                    Prix <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="25000"
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="vehicle-price-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-1">
                    <Gauge className="w-4 h-4 text-zinc-500" />
                    Kilométrage
                  </Label>
                  <Input
                    type="number"
                    value={formData.mileage}
                    onChange={(e) => setFormData({ ...formData, mileage: e.target.value })}
                    placeholder="50000"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-1">
                    <Zap className="w-4 h-4 text-zinc-500" />
                    Puissance
                  </Label>
                  <Input
                    value={formData.power}
                    onChange={(e) => setFormData({ ...formData, power: e.target.value })}
                    placeholder="Ex: 326cv"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-1">
                    <Fuel className="w-4 h-4 text-zinc-500" />
                    Carburant
                  </Label>
                  <Select
                    value={formData.fuel_type}
                    onValueChange={(value) => setFormData({ ...formData, fuel_type: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {FUEL_TYPES.map((f) => (
                        <SelectItem key={f.value} value={f.value}>
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-1">
                    <Settings2 className="w-4 h-4 text-zinc-500" />
                    Transmission
                  </Label>
                  <Select
                    value={formData.transmission}
                    onValueChange={(value) => setFormData({ ...formData, transmission: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {TRANSMISSIONS.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-1">
                    <Palette className="w-4 h-4 text-zinc-500" />
                    Couleur
                  </Label>
                  <Input
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    placeholder="Ex: Noir, Gris..."
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-white flex items-center gap-1">
                    <MapPin className="w-4 h-4 text-zinc-500" />
                    Origine
                  </Label>
                  <Select
                    value={formData.origin}
                    onValueChange={(value) => setFormData({ ...formData, origin: value })}
                  >
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800">
                      {ORIGINS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-white flex items-center gap-2">
                  Description
                  <span className="text-xs text-zinc-500 font-normal">(affichée sur la fiche du véhicule)</span>
                </Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Ex: Importée depuis Berlin, véhicule en excellent état, entretien suivi carnet à jour, finition complète avec toit ouvrant et sièges cuir..."
                  rows={6}
                  data-testid="vehicle-description"
                  className="bg-zinc-800 border-zinc-700 text-white min-h-[140px]"
                />
              </div>
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {/* Images - Drag & Drop Zone */}
              <div className="space-y-2">
                <Label className="text-white flex items-center gap-1">
                  <ImageIcon className="w-4 h-4 text-neon-lime" />
                  Photos ({formData.images.length})
                </Label>
                
                {/* Drag & Drop Zone */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className={`
                    border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all
                    ${isDragging 
                      ? 'border-neon-lime bg-neon-lime/10' 
                      : 'border-zinc-700 hover:border-zinc-500 bg-zinc-800/50'
                    }
                    ${uploadingImages ? 'pointer-events-none opacity-50' : ''}
                  `}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept="image/jpeg,image/png,image/webp,image/avif"
                    onChange={handleFileInputChange}
                    className="hidden"
                  />
                  
                  {uploadingImages ? (
                    <div className="flex flex-col items-center gap-2">
                      <Loader2 className="w-8 h-8 text-neon-lime animate-spin" />
                      <p className="text-zinc-400 text-sm">Upload en cours...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload className={`w-8 h-8 ${isDragging ? 'text-neon-lime' : 'text-zinc-500'}`} />
                      <p className="text-zinc-400 text-sm">
                        <span className="text-neon-lime font-medium">Glissez vos photos ici</span>
                        <br />ou cliquez pour sélectionner
                      </p>
                      <p className="text-zinc-600 text-xs">
                        JPEG, PNG, WebP, AVIF • Max 10MB par image
                      </p>
                    </div>
                  )}
                </div>

                {/* URL fallback */}
                <div className="flex gap-2">
                  <Input
                    value={newImageUrl}
                    onChange={(e) => setNewImageUrl(e.target.value)}
                    placeholder="Ou collez une URL d'image"
                    className="bg-zinc-800 border-zinc-700 text-white flex-1 text-sm"
                  />
                  <Button
                    onClick={addImage}
                    type="button"
                    variant="outline"
                    className="border-zinc-700 text-zinc-400"
                    size="sm"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>

                {/* Images Grid */}
                {formData.images.length > 0 && (
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {formData.images.map((img, index) => (
                      <div key={index} className="relative group aspect-video bg-zinc-800 rounded overflow-hidden">
                        <img
                          src={img}
                          alt={`Photo ${index + 1}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                        <button
                          onClick={() => removeImage(index)}
                          type="button"
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                        {index === 0 && (
                          <span className="absolute bottom-1 left-1 bg-neon-lime text-black text-xs px-1 rounded">
                            Principal
                          </span>
                        )}
                        <span className="absolute bottom-1 right-1 bg-black/60 text-white text-xs px-1 rounded">
                          {index + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-zinc-500 text-xs">
                  La première image sera l'image principale. Glissez pour réordonner.
                </p>
              </div>

              {/* Features */}
              <div className="space-y-2">
                <Label className="text-white">Équipements / Options</Label>
                <div className="flex gap-2">
                  <Input
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    placeholder="Ex: Toit ouvrant, GPS..."
                    className="bg-zinc-800 border-zinc-700 text-white flex-1"
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())}
                  />
                  <Button
                    onClick={addFeature}
                    type="button"
                    className="bg-zinc-700 text-white"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.features.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.features.map((feature, index) => (
                      <span
                        key={index}
                        className="bg-zinc-800 text-zinc-300 px-2 py-1 text-sm flex items-center gap-1"
                      >
                        {feature}
                        <button
                          onClick={() => removeFeature(index)}
                          className="text-zinc-500 hover:text-red-400"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Toggles */}
              <div className="space-y-4 pt-4 border-t border-zinc-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-zinc-500" />
                    <Label className="text-white">Visible sur le site</Label>
                  </div>
                  <Switch
                    checked={formData.is_available}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_available: checked })
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-neon-lime" />
                    <Label className="text-white">Mettre à la une</Label>
                  </div>
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_featured: checked })
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 text-red-500 font-bold text-sm">●</span>
                    <Label className="text-white">Véhicule vendu (bannière VENDUE)</Label>
                  </div>
                  <Switch
                    checked={formData.is_sold}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_sold: checked })
                    }
                    data-testid="vehicle-sold-toggle"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 text-orange-400 font-bold text-sm">★</span>
                    <Label className="text-white">Bonne affaire (badge discret)</Label>
                  </div>
                  <Switch
                    checked={formData.is_good_deal}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, is_good_deal: checked })
                    }
                    data-testid="vehicle-gooddeal-toggle"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4 pt-4 border-t border-zinc-800">
            <Button
              onClick={() => setDialogOpen(false)}
              variant="outline"
              className="flex-1 border-zinc-700 text-white"
            >
              Annuler
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 bg-neon-lime text-black font-bold"
              data-testid="save-vehicle-btn"
            >
              {saving ? "Enregistrement..." : editingVehicle ? "Mettre à jour" : "Ajouter"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-unbounded text-xl text-red-500 flex items-center gap-2">
              <Trash2 className="w-6 h-6" />
              Supprimer ce véhicule ?
            </DialogTitle>
          </DialogHeader>
          <p className="text-zinc-400 py-4">
            Êtes-vous sûr de vouloir supprimer{" "}
            <span className="text-white font-bold">
              {vehicleToDelete?.brand} {vehicleToDelete?.model}
            </span>{" "}
            ? Cette action est irréversible.
          </p>
          <div className="flex gap-4">
            <Button
              onClick={() => setDeleteConfirmOpen(false)}
              variant="outline"
              className="flex-1 border-zinc-700 text-white"
            >
              Annuler
            </Button>
            <Button
              onClick={handleDelete}
              className="flex-1 bg-red-500 text-white font-bold"
            >
              Supprimer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVehiclesPage;
